import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "services/api";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";

// Material Kit 2 React example components
import DefaultNavbar from "components/Navbars";
import SimpleFooter from "examples/Footers/SimpleFooter";

// Material Kit 2 React page layout routes
import routes from "routes";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function SignInBasic() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearStoredAuth = () => {
    [
      "access_token",
      "refresh_token",
      "access",
      "refresh",
      "accessToken",
      "refreshToken",
      "jwt_access",
      "jwt_refresh",
      "token",
      "jwt",
      "jwt_token",
      "auth_token",
      "user",
      "auth_persistence",
    ].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!email || !password) {
      setError(t("auth.missingCredentials") || "请输入邮箱和密码");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("auth/login/", {
        username: email,
        password,
      });

      const { access, refresh } = response.data || {};

      clearStoredAuth();
      sessionStorage.setItem("auth_persistence", "session");
      if (access) {
        sessionStorage.setItem("access_token", access);
      }
      if (refresh) {
        sessionStorage.setItem("refresh_token", refresh);
      }
      // 登录成功后跳转回首页（后续可以改为用户中心）
      window.location.href = "/userinfo/";
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        "";
      setError(detail || "登录失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DefaultNavbar routes={routes} transparent light />
      <MKBox
        position="absolute"
        top={0}
        left={0}
        zIndex={1}
        width="100%"
        minHeight="100vh"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <MKBox px={1} width="100%" height="100vh" mx="auto" position="relative" zIndex={2}>
        <Grid container spacing={1} justifyContent="center" alignItems="center" height="100%">
          <Grid item xs={11} sm={9} md={5} lg={4} xl={3}>
            <Card>
              <MKBox
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                mx={2}
                mt={-3}
                py={5}
                px={4}
                mb={1}
                textAlign="center"
              >
                <MKTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                  {t("auth.signInTitle")}
                </MKTypography>
              </MKBox>
              <MKBox pt={4} pb={3} px={3}>
                <MKBox component="form" role="form" onSubmit={handleSubmit} autoComplete="on">
                  <MKBox mb={2}>
                    <MKInput
                      type="email"
                      label={t("auth.emailLabel")}
                      fullWidth
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      id="username"
                      name="username"
                      autoComplete="username"
                      InputLabelProps={{ shrink: true }}
                    />
                  </MKBox>
                  <MKBox mb={2}>
                    <MKInput
                      type={showPassword ? "text" : "password"}
                      label={t("auth.passwordLabel")}
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              edge="end"
                              onClick={() => setShowPassword((current) => !current)}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </MKBox>
                  <MKBox mt={4} mb={1}>
                    <MKButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      type="submit"
                      disabled={loading}
                    >
                      {loading
                        ? t("auth.signInButtonLoading") || t("auth.signInButton")
                        : t("auth.signInButton")}
                    </MKButton>
                  </MKBox>
                  {error && (
                    <MKBox mt={1}>
                      <MKTypography variant="button" color="error" textAlign="center">
                        {error}
                      </MKTypography>
                    </MKBox>
                  )}
                  <MKBox mt={3} mb={1} textAlign="center">
                    <MKTypography variant="button" color="text">
                      {t("auth.noAccount")}{" "}
                      <MKTypography
                        component={Link}
                        to="/signup/"
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        textGradient
                      >
                        {t("auth.signUpLink")}
                      </MKTypography>
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </MKBox>
            </Card>
          </Grid>
        </Grid>
      </MKBox>
      <MKBox width="100%" position="absolute" zIndex={2} bottom="1.625rem">
        <SimpleFooter light />
      </MKBox>
    </>
  );
}

export default SignInBasic;
